/**
 * TestlabRealDataPage - Interface för testning med riktig skattekontodata
 */

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Play, Square, FileText, AlertCircle, Database, Zap, CheckCircle2, XCircle, BookOpen } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { RealDataUploader } from '@/components/RealDataUploader';
import { RateLimitCountdown } from '@/components/RateLimitCountdown';
import { TaxAnalysisRulesPanel } from '@/components/TaxAnalysisRulesPanel';
import { TestScenarioRunner } from '@/modules/audit-full-analysis/TestScenarioRunner';
import { RevisionOrchestrator } from '@/modules/audit-full-analysis/RevisionOrchestrator';
import { makeBatches } from '@/lib/utils/tokens';
import { MINI_MAX_INPUT_TOKENS, EST_TOKENS_PER_ITEM } from '@/modules/audit-full-analysis/config';
import { ParsedCSVData, normalizeSwedishDate, normalizeSwedishNumber, normalizeHeaderName } from '@/lib/csv-parser';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';

export function TestlabRealDataPage() {
  const [isRunning, setIsRunning] = useState(false);
  const [activeRevisions, setActiveRevisions] = useState<string[]>([]);
  const [stats, setStats] = useState<any>(null);
  const [dummyReport, setDummyReport] = useState<string>('');
  const [testStartTime, setTestStartTime] = useState<Date | null>(null);
  const [polledOnce, setPolledOnce] = useState(false);
      const [isAuthenticated, setIsAuthenticated] = useState(false);
      const [uploadedData, setUploadedData] = useState<any[] | null>(null);
      const [csvMeta, setCsvMeta] = useState<ParsedCSVData | null>(null);
      const [fieldMapping, setFieldMapping] = useState<Record<string, string>>({});
      const [diagnostics, setDiagnostics] = useState<any>({});
      const [lastError, setLastError] = useState<string>('');
  const [lastSeenCompletedRevisions, setLastSeenCompletedRevisions] = useState<Set<string>>(new Set());
  
  // Token-baserad kö-position (Steg 6)
  const [queuePosition, setQueuePosition] = useState<{
    position: number;
    estimated_wait_minutes: number;
    total_tpm: number;
  } | null>(null);
  
  // Lokal preflight preview state
  const [preflightStatus, setPreflightStatus] = useState<{
    dateCoverage: number;
    amountCoverage: number;
    eventCoverage: number;
    sampleTxns: any[];
    canStart: boolean;
    errors: string[];
  } | null>(null);
  
  // Företagskonfiguration för skattekalender
  const [companySize, setCompanySize] = useState<'small' | 'large'>('small');
  const [vatReportingPeriod, setVatReportingPeriod] = useState<'monthly' | 'quarterly'>('monthly');
  const [showRulesPanel, setShowRulesPanel] = useState(false);
  const [verboseLogging, setVerboseLogging] = useState(false);

  const scenarioRunnerRef = React.useRef<TestScenarioRunner | null>(null);
  const getRunner = () => {
    if (!scenarioRunnerRef.current) scenarioRunnerRef.current = new TestScenarioRunner();
    return scenarioRunnerRef.current;
  };

  // Auth status tracking
  useEffect(() => {
    let mounted = true;
    supabase.auth.getUser().then(({ data: { user } }) => {
      if (mounted) setIsAuthenticated(!!user);
    });
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setIsAuthenticated(!!session?.user);
    });
    return () => {
      mounted = false;
      subscription?.unsubscribe();
    };
  }, []);

  // Starta bakgrundsworker automatiskt för att plocka upp ev. köade jobb
  useEffect(() => {
    getRunner()
      .ensureWorkersRunning()
      .then(() => getRunner().kickWorkers().catch(() => {}))
      .catch(() => {});
    
    // Extra polling under första 20 sekunder för att nudge workers
    let kickCount = 0;
    const kickInterval = setInterval(async () => {
      kickCount++;
      if (kickCount <= 10) { // 10 x 2s = 20s
        console.log(`🔔 Polling kick #${kickCount}/10...`);
        try {
          await getRunner().kickWorkers();
        } catch (e) {
          console.warn('Kick error:', e);
        }
      } else {
        clearInterval(kickInterval);
      }
    }, 2000);
    
    return () => clearInterval(kickInterval);
  }, []);

  const handleDataReady = async (csvData: ParsedCSVData, mapping: Record<string, string>) => {
    console.log('📁 Data ready:', csvData.validRows, 'rows with mapping:', mapping);
    // Använd råa normaliserade rader utan inpackning – AI behöver fälten platt
    setUploadedData(csvData.rows);
    setCsvMeta(csvData);
    setFieldMapping(mapping);
    
    // Kör lokal preflight preview
    runLocalPreflight(csvData.rows, csvData.headers, mapping);
  };

  /**
   * Kör lokal preflight-check på uppladdad data
   * Visar coverage och sample transaktioner INNAN analys startas
   */
  const runLocalPreflight = (data: any[], headers: string[], mapping: Record<string, string>) => {
    console.log('🔍 Running local preflight preview...', { rows: data.length, headers, mapping });
    
    if (!data || data.length === 0) {
      setPreflightStatus(null);
      return;
    }
    
    const sample = data.slice(0, Math.min(50, data.length));
    
    // Skapa orderedKeys: om ingen header-rad, använd explicit index-array
    let orderedKeys: string[];
    if (headers && headers.length > 0) {
      orderedKeys = headers;
    } else {
      // Ingen header-rad: skapa ["0", "1", "2", ...] baserat på antal kolumner
      const firstRow = data[0];
      if (firstRow) {
        const numCols = Object.keys(firstRow).length;
        orderedKeys = Array.from({ length: numCols }, (_, i) => String(i));
      } else {
        orderedKeys = [];
      }
    }
    
    console.log('[Preflight] orderedKeys:', orderedKeys);
    console.log('[Preflight] fieldMapping:', mapping);
    console.log('[Preflight] first row keys:', data[0] ? Object.keys(data[0]) : []);

    // Hjälpare: resolva mapping till faktiska keys och värden (Kolumn N, fuzzy)
    const resolveFromMapping = (row: any, src: string | undefined) => {
      console.log('[resolveFromMapping] src:', src, 'row keys:', Object.keys(row));
      
      if (!src) return { raw: null as any, key: undefined as string | undefined };
      let key = src;
      let raw: any = row?.[key];

      // Fallback 1: Kolumn N → numeriskt index
      if (raw === undefined || raw === null) {
        const m = /^Kolumn\s+(\d+)$/i.exec(String(src));
        if (m) {
          const colNum = Number(m[1]); // 1-based från UI
          const idx = colNum - 1; // 0-based index
          
          console.log(`[resolveFromMapping] Kolumn ${colNum} → index ${idx}`);
          
          // Försök 1: orderedKeys[idx]
          const keyAtIdx = orderedKeys[idx];
          if (keyAtIdx !== undefined) {
            key = keyAtIdx;
            raw = row?.[keyAtIdx];
            console.log(`  → resolved via orderedKeys: "${keyAtIdx}" = ${raw}`);
          }
          
          // Försök 2: direkt numerisk key om fortfarande undefined
          if (raw === undefined || raw === null) {
            const numKey = String(idx);
            if (row.hasOwnProperty(numKey)) {
              key = numKey;
              raw = row[numKey];
              console.log(`  → resolved via numeric key "${numKey}" = ${raw}`);
            }
          }
        }
      }

      // Fallback 2: Fuzzy header-matchning (om headers finns)
      if ((raw === undefined || raw === null) && headers && headers.length > 0) {
        const norm = normalizeHeaderName(src);
        const match = orderedKeys.find(k => normalizeHeaderName(k) === norm);
        if (match) {
          key = match;
          raw = row?.[match];
          console.log(`[resolveFromMapping] Fuzzy match: "${src}" → "${match}" = ${raw}`);
        }
      }

      return { raw, key };
    };
    
    // Normalisera transaktioner samma sätt som workern
    const normalizedTxns = sample.map((row, i) => {
      const d = resolveFromMapping(row, mapping.Datum);
      const a = resolveFromMapping(row, mapping.Belopp);
      const e = resolveFromMapping(row, mapping.Händelse);
      
      return {
        id: `txn_${i}`,
        date_raw: d.raw,
        date: normalizeSwedishDate(String(d.raw || '')),
        amount_raw: a.raw,
        amount: normalizeSwedishNumber(String(a.raw || '')),
        event_raw: e.raw,
        event: e.raw,
        fromKey: {
          date: mapping.Datum,
          date_resolved: d.key,
          amount: mapping.Belopp,
          amount_resolved: a.key,
          event: mapping.Händelse,
          event_resolved: e.key,
        }
      };
    });
    
    const dateCov = normalizedTxns.filter(t => t.date).length / normalizedTxns.length;
    const amountCov = normalizedTxns.filter(t => t.amount !== null && t.amount !== undefined).length / normalizedTxns.length;
    const eventCov = normalizedTxns.filter(t => t.event && String(t.event).trim() !== '').length / normalizedTxns.length;
    
    const errors: string[] = [];
    if (dateCov < 0.7) errors.push(`Datum-täckning för låg: ${Math.round(dateCov * 100)}% (krav: 70%)`);
    if (amountCov < 0.9) errors.push(`Belopp-täckning för låg: ${Math.round(amountCov * 100)}% (krav: 90%)`);
    
    const canStart = dateCov >= 0.7 && amountCov >= 0.9;
    
    console.log('📊 Local preflight result:', { dateCov, amountCov, eventCov, canStart, errors });
    
    setPreflightStatus({
      dateCoverage: dateCov,
      amountCoverage: amountCov,
      eventCoverage: eventCov,
      sampleTxns: normalizedTxns.slice(0, 3),
      canStart,
      errors
    });
    
    if (!canStart) {
      toast({
        title: 'Mappningen behöver justeras',
        description: errors.join(' '),
        variant: 'destructive'
      });
    }
  };

  const cancelAllRevisions = async () => {
    // Use clearActiveTests for proper cleanup
    await clearActiveTests();
  };

  const startRealDataTest = async () => {
    console.log('🔥 START: startRealDataTest called');
    setLastError(''); // Clear previous errors
    
    // Validera lokal preflight
    if (preflightStatus && !preflightStatus.canStart) {
      toast({
        title: 'Kan inte starta analysen',
        description: 'Mappningen uppfyller inte kvalitetskrav. Se preflight-förhandsvisning och justera fältmappning.',
        variant: 'destructive',
      });
      return;
    }
    
    if (!uploadedData || !fieldMapping) {
      const error = 'Ingen data uppladdad - uploadedData eller fieldMapping saknas';
      console.error('❌ VALIDATION ERROR:', error, { uploadedData: !!uploadedData, fieldMapping });
      setLastError(error);
      toast({
        title: 'Ingen data uppladdad',
        description: 'Ladda upp CSV-fil och konfigurera fältmappning först',
        variant: 'destructive',
      });
      return;
    }

    try {
      console.log('🔍 STEP 1: Checking user authentication...');
      const { data: { user }, error: authError } = await supabase.auth.getUser();
      if (authError) {
        console.error('❌ AUTH ERROR:', authError);
        setLastError(`Auth error: ${authError.message}`);
        return;
      }
      if (!user) {
        const error = 'Ingen användare inloggad';
        console.error('❌ NO USER:', error);
        setLastError(error);
        toast({
          title: 'Logga in krävs',
          description: 'Du måste vara inloggad för att köra analysen.',
          variant: 'destructive',
        });
        return;
      }
      console.log('✅ User authenticated:', user.id, user.email);

      // Förhindra dubbstarter: starta inte om aktiv revision finns
      const { data: existingActive } = await supabase
        .from('revisions')
        .select('id')
        .eq('user_id', user.id)
        .not('status', 'in', '(completed,cancelled)')
        .limit(1);
      if ((existingActive || []).length > 0) {
        toast({
          title: 'Analys redan igång',
          description: 'Det finns redan en aktiv analys. Rensa eller vänta klart innan du startar en ny.',
          variant: 'destructive',
        });
        return;
      }

      setIsRunning(true);
      setTestStartTime(new Date());
      console.log('🚀 STEP 2: Starting real data analysis with', uploadedData.length, 'records...');
      
      // Starta workers innan analys börjar
      await getRunner().ensureWorkersRunning();
      
      console.log('🔍 STEP 3: Calling getRunner().runRealDataTest...');
      // Skapa företagskonfiguration för skattekalender
      const companyConfig = {
        companySize,
        vatReportingPeriod
      };
      console.log('📋 Company config:', companyConfig);
      
      // Använd real data med fältmappning och företagskonfiguration
      const revisionIds = await getRunner().runRealDataTest({
        name: "Real Data Test",
        revisions: 1,
        batches_per_revision: makeBatches(uploadedData, MINI_MAX_INPUT_TOKENS, EST_TOKENS_PER_ITEM).length,
        tokens_per_batch: 2000, // Uppskattning
        simulate_failures: false,
        cost_limit_test: false
      }, (csvMeta ?? {
        rows: uploadedData,
        validRows: uploadedData.length,
        totalRows: uploadedData.length,
        headers: Array.isArray(uploadedData) && uploadedData[0] ? Object.keys(uploadedData[0]) : [],
        errors: [],
        delimiter: ';',
        encoding: 'utf-8'
      }), fieldMapping, companyConfig);
      console.log('🆔 STEP 4: Got revision IDs:', revisionIds);
      setActiveRevisions(revisionIds);
      
      const actualBatchCount = makeBatches(uploadedData, MINI_MAX_INPUT_TOKENS, EST_TOKENS_PER_ITEM).length;
      console.log('✅ STEP 5: Success! Analysis started');
      toast({
        title: 'Analys startad',
        description: `Analyserar ${uploadedData.length} rader med AI (${actualBatchCount} ${actualBatchCount === 1 ? 'batch' : 'batcher'})`,
      });
    } catch (error: any) {
      console.error('❌ CATCH: Real data test error:', error);
      const errorMsg = error?.message || String(error) || 'Ett okänt fel uppstod';
      setLastError(`Exception: ${errorMsg}`);
      toast({
        title: 'Kunde inte starta analysen',
        description: errorMsg,
        variant: 'destructive',
      });
      setIsRunning(false);
    }
  };

  const viewReport = async (revisionId: string) => {
    try {
      // Fetch latest report row (handle multiple versions)
      const { data: report, error } = await supabase
        .from('revision_reports')
        .select('*')
        .eq('revision_id', revisionId)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error || !report) {
        toast({
          title: 'Rapport inte tillgänglig',
          description: 'Kunde inte hämta rapporten. Försök igen senare.',
          variant: 'destructive',
        });
        return;
      }

      // Prefer pre-rendered HTML if available
      if (report.report_html && typeof report.report_html === 'string' && report.report_html.trim().length > 0) {
        setDummyReport(report.report_html);
        toast({ title: 'Rapport hämtad', description: 'Slutrapporten visas nedan' });
        return;
      }

      // Fallback: build minimal HTML from JSON structure
      const reportJson = (typeof report.report_json === 'object' && report.report_json !== null) ? report.report_json as any : {};
      let body = '';

      if (reportJson.executive_summary) {
        body += `<section><h2>Sammanfattning</h2><p>${String(reportJson.executive_summary).replace(/\\n/g, '<br/>')}</p></section>`;
      }

      if (reportJson.module_sections) {
        body += `<section><h2>Modulrapporter</h2>`;
        if (Array.isArray(reportJson.module_sections)) {
          for (const section of reportJson.module_sections) {
            body += `<h3>${section.module_name || section.module || 'Modul'}</h3><p>${String(section.content || '').replace(/\\n/g, '<br/>')}</p>`;
          }
        } else if (typeof reportJson.module_sections === 'object') {
          for (const [module, content] of Object.entries(reportJson.module_sections)) {
            body += `<h3>${module}</h3><p>${String(content || '').replace(/\\n/g, '<br/>')}</p>`;
          }
        }
        body += `</section>`;
      }

      if (reportJson.recommendations) {
        body += `<section><h2>Rekommendationer</h2><p>${String(reportJson.recommendations).replace(/\\n/g, '<br/>')}</p></section>`;
      }

      const htmlDoc = `<!DOCTYPE html><html lang="sv"><head><meta charset="UTF-8"><title>AI-Analysrapport</title><meta name="viewport" content="width=device-width, initial-scale=1.0"></head><body>${body || '<p>Ingen rapport tillgänglig</p>'}</body></html>`;

      setDummyReport(htmlDoc);
      toast({ title: 'Rapport hämtad', description: 'Slutrapporten visas nedan' });
    } catch (error) {
      console.error('Error fetching report:', error);
      toast({
        title: 'Fel',
        description: 'Ett fel uppstod vid hämtning av rapporten',
        variant: 'destructive',
      });
    }
  };

  // Diagnostics function
  const runDiagnostics = async () => {
    console.log('🔧 Running diagnostics...');
    const diagnosticData: any = {};
    
    try {
      // 1. Check auth
      const { data: { user }, error: authError } = await supabase.auth.getUser();
      diagnosticData.auth = {
        user: user ? { id: user.id, email: user.email } : null,
        error: authError?.message || null,
        isAuthenticated: !!user
      };

      // 2. Check data state
      diagnosticData.dataState = {
        uploadedData: uploadedData ? uploadedData.length : null,
        fieldMapping: Object.keys(fieldMapping || {}).length,
        mappingKeys: Object.keys(fieldMapping || {}),
        isRunning,
        activeRevisions: activeRevisions.length
      };

      // 2b. Check actual active revisions from database
      try {
        const { data: activeDbRevisions, error: activeRevisionsError } = await supabase
          .from('revisions')
          .select('id, status')
          .not('status', 'in', '(completed,cancelled)')
          .order('created_at', { ascending: false });
        
        diagnosticData.actualActiveRevisions = {
          count: activeDbRevisions?.length || 0,
          revisions: activeDbRevisions || [],
          error: activeRevisionsError?.message || null
        };
      } catch (err: any) {
        diagnosticData.actualActiveRevisions = {
          count: 0,
          revisions: [],
          error: err.message
        };
      }

      // 3. Check database access - get recent revisions with details
      try {
        const { data: revisionsData, error: revError } = await supabase
          .from('revisions')
          .select('id, company_name, status, last_error, updated_at, started_at, total_cost_sek, cost_cap_sek, org_number')
          .order('updated_at', { ascending: false })
          .limit(5);
        const activeCount = (revisionsData || []).filter(r => !['completed','cancelled'].includes(r.status)).length;
        diagnosticData.database = {
          revisionsAccess: !revError,
          revisionsError: revError?.message || null,
          revisionsCount: revisionsData?.length || 0,
          revisionsActiveCount: activeCount,
          revisionsSample: revisionsData || []
        };

        // Fetch details for latest revision
        const latestRevId = revisionsData?.[0]?.id;
        if (latestRevId) {
          const { data: batchesForLatest, error: batchLatestErr } = await supabase
            .from('batches')
            .select('state, module, retry_after, last_error')
            .eq('revision_id', latestRevId);
          diagnosticData.latestRevision = {
            id: latestRevId,
            batches: batchesForLatest || [],
            batchesError: batchLatestErr?.message || null,
          };

          const { data: wqData, error: wqErr } = await supabase
            .from('worker_queue')
            .select('status, queue_name, job_type, job_id, last_error')
            .order('created_at', { ascending: false })
            .limit(5);
          diagnosticData.workerQueue = {
            access: !wqErr,
            error: wqErr?.message || null,
            sample: wqData || [],
          };
        }
      } catch (dbErr: any) {
        diagnosticData.database = {
          revisionsAccess: false,
          revisionsError: dbErr.message,
          revisionsCount: 0
        };
      }

      // 4. Check worker tables
      try {
        const { data: workersData, error: workerError } = await supabase
          .from('workers')
          .select('id, status')
          .limit(3);
        diagnosticData.workers = {
          access: !workerError,
          error: workerError?.message || null,
          count: workersData?.length || 0
        };
      } catch (workerErr: any) {
        diagnosticData.workers = {
          access: false,
          error: workerErr.message,
          count: 0
        };
      }

      // 5. Check batches access
      try {
        const { data: batchesData, error: batchError } = await supabase
          .from('batches')
          .select('batch_id, state')
          .limit(3);
        diagnosticData.batches = {
          access: !batchError,
          error: batchError?.message || null,
          count: batchesData?.length || 0
        };
      } catch (batchErr: any) {
        diagnosticData.batches = {
          access: false,
          error: batchErr.message,
          count: 0
        };
      }

      diagnosticData.timestamp = new Date().toISOString();
      diagnosticData.lastError = lastError;
      
      console.log('📊 Diagnostics result:', diagnosticData);
      setDiagnostics(diagnosticData);
      
      toast({
        title: 'Diagnostik klar',
        description: 'Se diagnostikrutan för detaljer',
      });
    } catch (err: any) {
      console.error('❌ Diagnostics error:', err);
      setDiagnostics({ error: err.message, timestamp: new Date().toISOString() });
    }
  };

  // Simple test function without file upload
  const testMinimalRevision = async () => {
    console.log('🧪 Testing minimal revision creation...');
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        toast({ title: 'Ej inloggad', variant: 'destructive' });
        return;
      }

      console.log('📝 Creating minimal revision directly...');
      const { data: revision, error } = await supabase
        .from('revisions')
        .insert({
          user_id: user.id,
          org_number: '559999-9999',
          company_name: 'Minimal Test Revision',
          cost_cap_sek: 100,
          status: 'initializing'
        })
        .select()
        .single();

      if (error) {
        console.error('❌ Minimal revision error:', error);
        setLastError(`Minimal revision error: ${error.message}`);
        toast({
          title: 'Minimal revision misslyckades',
          description: error.message,
          variant: 'destructive',
        });
      } else {
        console.log('✅ Minimal revision created:', revision);
        toast({
          title: 'Minimal revision skapad!',
          description: `Revision ID: ${revision.id}`,
        });
      }
    } catch (err: any) {
      console.error('❌ Minimal revision exception:', err);
      setLastError(`Minimal revision exception: ${err.message}`);
    }
  };

  const stopTest = async () => {
    try {
      await getRunner().stopTest();
      setIsRunning(false);
      setActiveRevisions([]);
      setTestStartTime(null);
    } catch (error) {
      console.error('❌ Stop test error:', error);
    }
  };

  const generateReport = async () => {
    if (activeRevisions.length > 0) {
      try {
        toast({ title: 'Genererar rapport...', description: 'Använder GPT-5-premium för att analysera findings' });
        const html = await getRunner().generateRealReport(activeRevisions[0]);
        setDummyReport(html);
        toast({ title: 'Rapport genererad', description: 'AI-analysrapport baserad på findings har genererats' });
      } catch (error: any) {
        toast({ 
          title: 'Kunde inte generera rapport', 
          description: error?.message || 'Ett fel uppstod', 
          variant: 'destructive' 
        });
      }
    } else {
      toast({ title: 'Ingen revision', description: 'Starta analysen först för att generera rapport.', variant: 'destructive' });
    }
  };

  const clearActiveTests = async () => {
    try {
      console.log('🧹 Starting clearActiveTests...');

      // Hämta aktuell användare
      const { data: { user }, error: authErr } = await supabase.auth.getUser();
      if (authErr) throw authErr;
      if (!user) {
        toast({ title: 'Ej inloggad', description: 'Logga in för att rensa analyser.', variant: 'destructive' });
        return;
      }
      
      // Steg 1: Hitta alla aktiva revisioner för användaren
      const { data: revisions, error: fetchError } = await supabase
        .from('revisions')
        .select('id, company_name, status')
        .eq('user_id', user.id)
        .not('status', 'in', '(completed,cancelled)');

      if (fetchError) {
        console.error('❌ Fetch error:', fetchError);
        throw fetchError;
      }
      
      const ids = (revisions || []).map(r => r.id);
      
      console.log(`🔍 Found ${ids.length} active revisions:`, revisions?.map(r => ({ id: r.id, name: r.company_name, status: r.status })));
      
      if (ids.length === 0) {
        toast({ title: 'Inget att rensa', description: 'Inga aktiva analyser hittades.' });
        return;
      }

      // Steg 2: Markera batcher som failed (tillåtna states enligt constraint)
      const { error: batchError } = await supabase
        .from('batches')
        .update({ 
          state: 'failed',
          last_error: 'Manually cleared by user',
          completed_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        })
        .in('revision_id', ids)
        .in('state', ['queued','processing','retry','rate_limited']);
      
      if (batchError) {
        console.warn('⚠️ Could not update batches:', batchError);
      }

      // Steg 3: Markera egna jobb som failed (säkerställer att köhantering inte återstartar)
      const { error: jobsError } = await supabase
        .from('jobs')
        .update({
          status: 'failed',
          error_message: 'Manually cleared by user',
          completed_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        })
        .eq('user_id', user.id)
        .not('status', 'in', '(completed,failed)');
      if (jobsError) {
        console.warn('⚠️ Could not update jobs:', jobsError);
      }
      
      // Steg 4: Ta bort aggregates och reports (cleanup)
      await supabase.from('aggregates').delete().in('revision_id', ids);
      await supabase.from('revision_reports').delete().in('revision_id', ids);

      // Steg 5: Markera alla revisions som cancelled
      const { error: revError } = await supabase
        .from('revisions')
        .update({ 
          status: 'cancelled', 
          last_error: 'Manually cleared by user',
          completed_at: new Date().toISOString(),
          updated_at: new Date().toISOString() 
        })
        .in('id', ids);
      
      if (revError) {
        console.error('❌ Could not update revisions:', revError);
        throw revError;
      }

      console.log(`✅ Successfully cleared ${ids.length} revisions`);
      setActiveRevisions([]);
      setIsRunning(false);
      
      // Uppdatera stats
      const currentStats = await getRunner().getRealtimeStats();
      setStats(currentStats);
      
      toast({ 
        title: 'Rensat', 
        description: `Rensade ${ids.length} aktiva analyser (revisioner, batchar och arbetskö).` 
      });
    } catch (err: any) {
      console.error('❌ Clear error:', err);
      toast({ 
        title: 'Kunde inte rensa', 
        description: err?.message || 'Ett fel uppstod. Se konsolen för detaljer.', 
        variant: 'destructive' 
      });
    }
  };

  const forceClearAll = async () => {
    try {
      console.log('🔥 Force clearing all active revisions via edge function...');
      
      const { data, error } = await supabase.functions.invoke('force-clear-revisions', {
        body: {}
      });

      if (error) {
        console.error('❌ Force clear error:', error);
        throw error;
      }

      console.log('✅ Force clear response:', data);
      
      setActiveRevisions([]);
      setIsRunning(false);
      
      // Uppdatera stats
      const currentStats = await getRunner().getRealtimeStats();
      setStats(currentStats);
      
      toast({ 
        title: 'Hårdrensat', 
        description: data?.message || `Tvångsrensade ${data?.cleared || 0} analyser via backend.` 
      });
    } catch (err: any) {
      console.error('❌ Force clear error:', err);
      toast({ 
        title: 'Kunde inte hårdnsa', 
        description: err?.message || 'Ett fel uppstod. Se konsolen för detaljer.', 
        variant: 'destructive' 
      });
    }
  };

  // Real-time stats polling - starts immediately to show existing active revisions
  useEffect(() => {
    let interval: NodeJS.Timeout;
    
    const fetchStats = async () => {
      console.log('🔄 Fetching stats, activeRevisions:', activeRevisions.length);
      try {
        const currentStats = await getRunner().getRealtimeStats();
        console.log('📊 Got stats:', currentStats);
        
        // Check for newly completed revisions and show toast
        if (currentStats?.revisions) {
          for (const rev of currentStats.revisions) {
            if (rev.status === 'completed' && !lastSeenCompletedRevisions.has(rev.id)) {
              toast({
                title: '✅ Test färdigställt',
                description: `Analys av ${rev.company_name} är klar. ${rev.progress?.batches_completed || 0} batchar slutförda.`,
              });
              setLastSeenCompletedRevisions(prev => new Set(prev).add(rev.id));
            }
          }
          
          // Remove completed revisions from activeRevisions to stop polling
          const completedIds = currentStats.revisions
            .filter((r: any) => r.status === 'completed')
            .map((r: any) => r.id);
          
          if (completedIds.length > 0) {
            setActiveRevisions(prev => prev.filter(id => !completedIds.includes(id)));
          }
        }
        
        setStats(currentStats);
        
        // Stoppa workers om inga aktiva revisioner finns
        if (currentStats?.active_revisions === 0 && isRunning) {
          console.log('✅ Alla revisioner klara – stoppar workers');
          await getRunner().stopWorkersIfIdle();
          setIsRunning(false);
        }
        
        // Kick background workers if we see a revision stuck in "batching" with queued batches
        if (currentStats?.revisions?.some((r: any) => r.status === 'batching' && (r.progress?.batches_total || 0) > 0)) {
          console.log('🦶 Kick workers: batching detected with queued batches');
          getRunner().kickWorkers().catch((e) => console.warn('kickWorkers error:', e));
        }
        setPolledOnce(true);
      } catch (error) {
        console.error('❌ Stats error:', error);
      }
    };

    // Always poll to detect existing active revisions
    fetchStats();
    interval = setInterval(fetchStats, 3000);

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [lastSeenCompletedRevisions, isRunning]);

  // Auto-run diagnostics on page load
  useEffect(() => {
    const timer = setTimeout(() => {
      console.log('🔧 Auto-running diagnostics on page load...');
      runDiagnostics();
    }, 1000);
    return () => clearTimeout(timer);
  }, []);

  // Steg 6 & 7: Hämta kö-position och realtime uppdateringar
  useEffect(() => {
    if (!activeRevisions || activeRevisions.length === 0) {
      setQueuePosition(null);
      return;
    }

    const firstActiveRevision = activeRevisions[0];
    
    const fetchQueuePosition = async () => {
      try {
        const orchestrator = new RevisionOrchestrator();
        const pos = await orchestrator.getQueuePosition(firstActiveRevision);
        setQueuePosition(pos);
      } catch (error) {
        console.error('❌ Queue position error:', error);
      }
    };
    
    // Initial fetch
    fetchQueuePosition();
    
    // Poll every 5 seconds
    const interval = setInterval(fetchQueuePosition, 5000);
    
    // Realtime updates från worker_queue
    const channel = supabase
      .channel('queue-realtime')
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'worker_queue'
      }, () => {
        console.log('🔄 Worker queue changed, recalculating position...');
        fetchQueuePosition();
      })
      .subscribe();
    
    return () => {
      clearInterval(interval);
      supabase.removeChannel(channel);
    };
  }, [activeRevisions]);

  // Dynamiskt batchantal från realtidsstatistik (token-baserad batching)
  const activeBatchStats = stats?.revisions?.[0]?.progress;
  const batchCount = activeBatchStats?.batches_total || 0;
  const completedBatches = activeBatchStats?.batches_completed || 0;
  const estimatedCost = batchCount * 0.5; // Uppskattad kostnad per batch

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      {/* Header */}
      <div className="bg-white border-b shadow-sm">
        <div className="max-w-6xl mx-auto px-6 py-4">
          <Link to="/testlab" className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground mb-4">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Tillbaka till Testlab
          </Link>
          
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold flex items-center gap-3">
                <Database className="h-8 w-8 text-primary" />
                AuditAI Real Data
              </h1>
              <p className="text-muted-foreground mt-1">
                Testmodul för fullständig AI-analys av riktig skattekontodata
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Badge variant="secondary" className="ml-auto">TESTLAB</Badge>
              <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                <Zap className="h-3 w-3 mr-1" />
                REAL AI
              </Badge>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-8 space-y-6">
        {/* Warning Banner */}
        <Alert className="border-amber-200 bg-amber-50">
          <AlertCircle className="h-4 w-4 text-amber-600" />
          <AlertDescription className="text-amber-800">
            <strong>Riktig AI-analys aktivt:</strong> Denna modul använder riktiga Azure OpenAI API-anrop
            som kostar pengar. Kostnader visas löpande under körning – kör med försiktighet.
          </AlertDescription>
        </Alert>

        {/* Diagnostics Panel */}
        <Card className="mb-6 border-yellow-200 bg-yellow-50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              🔧 TESTLAB DIAGNOSTIK
              <Badge variant="outline" className="bg-yellow-100 text-yellow-800">DEBUG</Badge>
            </CardTitle>
            <CardDescription>
              Diagnostikinformation för felsökning - tas bort i produktion
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex gap-2">
              <Button onClick={runDiagnostics} variant="outline" size="sm">
                Kör diagnostik
              </Button>
              <Button onClick={testMinimalRevision} variant="outline" size="sm">
                Test minimal revision
              </Button>
              <Button 
                onClick={() => setShowRulesPanel(!showRulesPanel)} 
                variant={showRulesPanel ? "default" : "outline"} 
                size="sm"
                className="ml-auto"
              >
                <BookOpen className="h-4 w-4 mr-2" />
                {showRulesPanel ? 'Dölj AI-regler' : 'Visa AI-regler'}
              </Button>
            </div>
            
            {diagnostics.timestamp && (
              <div className="bg-white p-4 rounded border text-xs space-y-3">
                <div className="text-green-600 font-medium">
                  Diagnostik körd: {new Date(diagnostics.timestamp).toLocaleString('sv-SE')}
                </div>
                
                {/* Auth Status */}
                <div>
                  <div className="font-medium text-blue-600">🔐 Authentication:</div>
                  <div className="ml-4 text-gray-600">
                    {diagnostics.auth?.isAuthenticated ? (
                      <div className="text-green-600">
                        ✅ Inloggad: {diagnostics.auth.user?.email} ({diagnostics.auth.user?.id})
                      </div>
                    ) : (
                      <div className="text-red-600">❌ Ej inloggad: {diagnostics.auth?.error}</div>
                    )}
                  </div>
                </div>

                {/* Data State */}
                <div>
                  <div className="font-medium text-blue-600">📊 Data State:</div>
                  <div className="ml-4 text-gray-600">
                    <div>Uppladdad data: {diagnostics.dataState?.uploadedData || 0} rader</div>
                    <div>Fältmappning: {diagnostics.dataState?.fieldMapping || 0} fält</div>
                    <div>Mapping keys: {JSON.stringify(diagnostics.dataState?.mappingKeys || [])}</div>
                    <div>Kör analys: {diagnostics.dataState?.isRunning ? 'JA' : 'NEJ'}</div>
                    <div>Aktiva revisioner: {diagnostics.dataState?.activeRevisions || 0}</div>
                  </div>
                </div>

                {/* Database Access */}
                <div>
                  <div className="font-medium text-blue-600">🗄️ Database Access:</div>
                  <div className="ml-4 text-gray-600 space-y-2">
                    <div>Revisions tabell: {diagnostics.database?.revisionsAccess ? '✅ OK' : '❌ FEL'}</div>
                    {diagnostics.database?.revisionsError && (
                      <div className="text-red-600">Error: {diagnostics.database.revisionsError}</div>
                    )}
                     <div>Revisioner i DB: {diagnostics.database?.revisionsCount || 0} • Aktiva: {diagnostics.database?.revisionsActiveCount ?? 0}</div>
                     <div className="font-semibold text-green-700">
                       🔄 Verkliga aktiva revisioner: {diagnostics.actualActiveRevisions?.count || 0}
                       {diagnostics.actualActiveRevisions?.error && (
                         <span className="text-red-600"> (Fel: {diagnostics.actualActiveRevisions.error})</span>
                       )}
                     </div>
                    {diagnostics.database?.revisionsSample?.length > 0 && (
                      <div className="text-xs bg-slate-50 border rounded p-2">
                        <div className="font-medium mb-1">Senaste revisioner</div>
                        <ul className="space-y-1">
                          {diagnostics.database.revisionsSample.map((r: any) => (
                            <li key={r.id} className="flex flex-col md:flex-row md:items-center md:justify-between gap-1">
                              <span className="truncate">
                                <span className={r.status === 'cancelled' ? 'text-red-600' : 'text-slate-700'}>
                                  {r.company_name || '—'}
                                </span>
                                {' • '}
                                <span className="font-mono">{r.id.slice(0, 8)}</span>
                                {' • '}
                                <span>Status: {r.status}</span>
                              </span>
                              <span className="text-slate-500">
                                Uppd: {r.updated_at ? new Date(r.updated_at).toLocaleString('sv-SE') : '—'}
                              </span>
                              {r.last_error && (
                                <span className="text-red-600">Fel: {String(r.last_error).slice(0, 160)}</span>
                              )}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                </div>

                {/* Senaste revision – Batcher */}
                {diagnostics.latestRevision && (
                  <div>
                    <div className="font-medium text-blue-600">⏱️ Senaste revision – Batcher:</div>
                    <div className="ml-4 text-gray-600">
                      {diagnostics.latestRevision.batches?.length > 0 ? (
                        <ul className="list-disc pl-5">
                          {diagnostics.latestRevision.batches.map((b: any, idx: number) => (
                            <li key={idx}>
                              {b.module} • {b.state}
                              {b.retry_after && <span> • Retry after: {new Date(b.retry_after).toLocaleTimeString('sv-SE')}</span>}
                              {b.last_error && <span className="text-red-600"> • Fel: {String(b.last_error).slice(0, 160)}</span>}
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <div>Inga batcher hittades för senaste revisionen.</div>
                      )}
                      {diagnostics.latestRevision.batchesError && (
                        <div className="text-red-600">Error: {diagnostics.latestRevision.batchesError}</div>
                      )}
                    </div>
                  </div>
                )}

                {/* Worker Queue (senaste) */}
                {diagnostics.workerQueue && (
                  <div>
                    <div className="font-medium text-blue-600">📥 Worker Queue (senaste):</div>
                    <div className="ml-4 text-gray-600">
                      <div>Access: {diagnostics.workerQueue?.access ? '✅ OK' : '❌ FEL'}</div>
                      {diagnostics.workerQueue?.error && (
                        <div className="text-red-600">Error: {diagnostics.workerQueue.error}</div>
                      )}
                      {diagnostics.workerQueue?.sample?.length > 0 && (
                        <ul className="list-disc pl-5">
                          {diagnostics.workerQueue.sample.map((j: any) => (
                            <li key={j.job_id}>
                              {j.queue_name}/{j.job_type} • {j.status} • <span className="font-mono">{j.job_id}</span>
                              {j.last_error && <span className="text-red-600"> • Fel: {String(j.last_error).slice(0, 160)}</span>}
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  </div>
                )}

                {/* Workers */}
                <div>
                  <div className="font-medium text-blue-600">⚙️ Workers Table:</div>
                  <div className="ml-4 text-gray-600">
                    <div>Access: {diagnostics.workers?.access ? '✅ OK' : '❌ FEL'}</div>
                    {diagnostics.workers?.error && (
                      <div className="text-red-600">Error: {diagnostics.workers.error}</div>
                    )}
                    <div>Count: {diagnostics.workers?.count || 0}</div>
                  </div>
                </div>

                {/* Batches */}
                <div>
                  <div className="font-medium text-blue-600">📦 Batches Table:</div>
                  <div className="ml-4 text-gray-600">
                    <div>Access: {diagnostics.batches?.access ? '✅ OK' : '❌ FEL'}</div>
                    {diagnostics.batches?.error && (
                      <div className="text-red-600">Error: {diagnostics.batches.error}</div>
                    )}
                    <div>Count: {diagnostics.batches?.count || 0}</div>
                  </div>
                </div>

                {/* Last Error */}
                {lastError && (
                  <div>
                    <div className="font-medium text-red-600">🚨 Senaste fel:</div>
                    <div className="ml-4 text-red-600 bg-red-50 p-2 rounded">
                      {lastError}
                    </div>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* AI Analysis Rules Panel - togglas med knapp */}
        {showRulesPanel && (
          <TaxAnalysisRulesPanel 
            companyConfig={{ 
              companySize, 
              vatReportingPeriod 
            }} 
          />
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 lg:grid-rows-2 gap-6">
          {/* Rad 1, kolumn 1: CSV-uppladdning med fältmappning */}
          <div className="lg:col-start-1 lg:row-start-1">
            {/* TODO: Testlab behöver egen uploader-implementation som inte använder storage */}
            <div className="p-8 border-2 border-dashed rounded-lg text-center text-muted-foreground">
              <p>RealDataUploader har migrerats till storage-baserad arkitektur.</p>
              <p className="text-sm mt-2">Testlab kräver egen implementation för direktparsning.</p>
            </div>
            {/* <RealDataUploader 
              onDataReady={handleDataReady}
              maxFileSize={20 * 1024 * 1024}
              acceptedFormats={['.csv', '.txt']}
              customerId="test-customer"
              moduleName="skattekonto"
            /> */}
          </div>

          {/* Preflight Preview Card (ny) - visas efter upload */}
          {uploadedData && fieldMapping && Object.keys(fieldMapping).length > 0 && preflightStatus && (
            <div className="lg:col-span-2">
              <Card className={preflightStatus.canStart ? "border-green-200 bg-green-50" : "border-amber-200 bg-amber-50"}>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    {preflightStatus.canStart ? <CheckCircle2 className="h-5 w-5 text-green-600" /> : <AlertCircle className="h-5 w-5 text-amber-600" />}
                    Preflight-förhandsvisning
                    <Badge variant={preflightStatus.canStart ? "default" : "destructive"}>
                      {preflightStatus.canStart ? "Redo att köra" : "Behöver justering"}
                    </Badge>
                  </CardTitle>
                  <CardDescription>
                    Lokal validering av fältmappning innan AI-analys startas
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Coverage Metrics */}
                  <div className="grid grid-cols-3 gap-4">
                    <div className="space-y-1">
                      <div className="text-sm font-medium">Datum-täckning</div>
                      <div className="flex items-center gap-2">
                        <Progress value={preflightStatus.dateCoverage * 100} className="h-2" />
                        <span className={`text-sm font-mono ${preflightStatus.dateCoverage >= 0.7 ? 'text-green-600' : 'text-red-600'}`}>
                          {Math.round(preflightStatus.dateCoverage * 100)}%
                        </span>
                      </div>
                      <div className="text-xs text-muted-foreground">Krav: ≥70%</div>
                    </div>
                    <div className="space-y-1">
                      <div className="text-sm font-medium">Belopp-täckning</div>
                      <div className="flex items-center gap-2">
                        <Progress value={preflightStatus.amountCoverage * 100} className="h-2" />
                        <span className={`text-sm font-mono ${preflightStatus.amountCoverage >= 0.9 ? 'text-green-600' : 'text-red-600'}`}>
                          {Math.round(preflightStatus.amountCoverage * 100)}%
                        </span>
                      </div>
                      <div className="text-xs text-muted-foreground">Krav: ≥90%</div>
                    </div>
                    <div className="space-y-1">
                      <div className="text-sm font-medium">Händelse-täckning</div>
                      <div className="flex items-center gap-2">
                        <Progress value={preflightStatus.eventCoverage * 100} className="h-2" />
                        <span className="text-sm font-mono text-muted-foreground">
                          {Math.round(preflightStatus.eventCoverage * 100)}%
                        </span>
                      </div>
                      <div className="text-xs text-muted-foreground">Frivillig</div>
                    </div>
                  </div>

                  {/* Sample Transactions */}
                  <div className="space-y-2">
                    <div className="text-sm font-medium">Första 3 normaliserade transaktioner:</div>
                    <div className="space-y-2">
                      {preflightStatus.sampleTxns.map((txn, idx) => (
                        <div key={idx} className="bg-white p-3 rounded border text-xs space-y-1">
                          <div className="flex justify-between items-start">
                            <div className="space-y-1 flex-1">
                              <div className="flex gap-4">
                                <span className={txn.date ? 'text-green-600 font-medium' : 'text-red-600 font-medium'}>
                                  Datum: {txn.date || '❌ MISSLYCKADES'}
                                </span>
                                <span className={txn.amount !== null ? 'text-green-600 font-medium' : 'text-red-600 font-medium'}>
                                  Belopp: {txn.amount !== null ? txn.amount : '❌ MISSLYCKADES'}
                                </span>
                                <span className="text-muted-foreground">
                                  Händelse: {txn.event || '(saknas)'}
                                </span>
                              </div>
                              <div className="text-muted-foreground">
                                Rådata: datum="{txn.date_raw}" | belopp="{txn.amount_raw}" | händelse="{txn.event_raw}"
                              </div>
                              <div className="text-muted-foreground text-[10px]">
                                Från kolumner: {txn.fromKey?.date} → {txn.fromKey?.amount} → {txn.fromKey?.event}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Errors */}
                  {preflightStatus.errors.length > 0 && (
                    <Alert variant="destructive">
                      <AlertCircle className="h-4 w-4" />
                      <AlertDescription>
                        <div className="font-medium mb-1">Mappningen behöver justeras:</div>
                        <ul className="list-disc list-inside space-y-1">
                          {preflightStatus.errors.map((err, idx) => (
                            <li key={idx}>{err}</li>
                          ))}
                        </ul>
                        <div className="mt-2 text-sm">
                          💡 Tips: Justera fältmappningen genom att ladda upp filen igen och välja rätt kolumner.
                        </div>
                      </AlertDescription>
                    </Alert>
                  )}
                </CardContent>
              </Card>
            </div>
          )}

          {/* Rad 2, kolumn 1: Analysinställningar */}
          {uploadedData && fieldMapping && Object.keys(fieldMapping).length > 0 && (
            <div className="lg:col-start-1 lg:row-start-2">
              <Card>
                <CardHeader>
                  <CardTitle>Analysinställningar</CardTitle>
                  <CardDescription>
                    Konfigurera och starta AI-analys med Azure OpenAI
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Company Configuration */}
                  <div className="space-y-3">
                    <div>
                      <label className="text-sm font-medium">Företagsstorlek</label>
                      <select 
                        value={companySize} 
                        onChange={(e) => setCompanySize(e.target.value as 'small' | 'large')}
                        className="w-full mt-1 p-2 border rounded"
                      >
                        <option value="small">Litet bolag</option>
                        <option value="large">Stort bolag</option>
                      </select>
                    </div>
                    <div>
                      <label className="text-sm font-medium">Momsperiod</label>
                      <select 
                        value={vatReportingPeriod} 
                        onChange={(e) => setVatReportingPeriod(e.target.value as 'monthly' | 'quarterly')}
                        className="w-full mt-1 p-2 border rounded"
                      >
                        <option value="monthly">Månadsvis</option>
                        <option value="quarterly">Kvartalsvis</option>
                      </select>
                    </div>
                    
                    {/* Verbose Logging Toggle */}
                    <div className="flex items-center gap-2 pt-2">
                      <Switch
                        id="verbose-logs"
                        checked={verboseLogging}
                        onCheckedChange={(checked) => {
                          setVerboseLogging(checked);
                          getRunner().workers.forEach(w => w.setVerboseLogging(checked));
                        }}
                      />
                      <Label htmlFor="verbose-logs" className="text-sm text-muted-foreground cursor-pointer">
                        Visa detaljerade worker-loggar (debug)
                      </Label>
                    </div>
                  </div>

                  <Button 
                    onClick={startRealDataTest}
                    disabled={isRunning || !uploadedData || (preflightStatus && !preflightStatus.canStart)}
                    className="w-full"
                  >
                    {isRunning ? 'Kör analys...' : 'Starta AI-analys'}
                  </Button>
                  
                  {preflightStatus && !preflightStatus.canStart && (
                    <Alert variant="destructive">
                      <AlertCircle className="h-4 w-4" />
                      <AlertDescription>
                        Mappningen uppfyller inte kvalitetskrav. Se preflight-förhandsvisning ovan och justera fältmappning.
                      </AlertDescription>
                    </Alert>
                  )}

                  {lastError && (
                    <Alert variant="destructive">
                      <AlertCircle className="h-4 w-4" />
                      <AlertDescription>{lastError}</AlertDescription>
                    </Alert>
                  )}

                  {isRunning && (
                    <div className="text-sm text-muted-foreground">
                      🤖 Analys pågår i bakgrunden...
                    </div>
                  )}

                  <Button 
                    onClick={cancelAllRevisions}
                    variant="destructive"
                    disabled={!isRunning && activeRevisions.length === 0 && !stats?.active_revisions}
                    className="w-full"
                  >
                    Rensa aktiva analyser
                  </Button>
                  
                  {stats?.active_revisions > 0 && (
                    <Button 
                      onClick={forceClearAll}
                      variant="outline"
                      className="w-full border-red-500 text-red-600 hover:bg-red-50"
                    >
                      🔥 Hårdnrensning (Backend)
                    </Button>
                  )}
                </CardContent>
              </Card>
            </div>
          )}

          {/* Kolumn 2: Färdigställda tester (spänner två rader) */}
          <Card className="lg:col-start-2 lg:row-start-1 lg:row-span-2 flex flex-col overflow-hidden lg:h-[600px]">
            <CardHeader>
              <CardTitle>Färdigställda tester</CardTitle>
              <CardDescription>
                Avslutade analyser från senaste körningarna
              </CardDescription>
            </CardHeader>
            <CardContent className="flex-1 min-h-0 overflow-y-auto">
              {stats?.revisions?.some((r: any) => r.status === 'completed') ? (
                <div className="space-y-3">
                  {(stats.revisions || [])
                    .filter((r: any) => r.status === 'completed')
                    .map((rev: any, idx: number) => (
                      <div key={`${rev.id}_completed_${idx}`} className="border rounded p-3 bg-green-50 border-green-200">
                        <div className="flex justify-between items-center mb-2">
                          <span className="font-medium">{rev.company_name}</span>
                          <Badge>completed</Badge>
                        </div>
                        {rev.progress && (
                          <div className="text-xs text-muted-foreground space-y-1">
                            <div className="flex justify-between">
                              <span>Batchar: {rev.progress.batches_completed || 0}/{rev.progress.batches_total || 0}</span>
                              <span>{Number(rev.total_cost_sek || 0).toFixed(2)} SEK</span>
                            </div>
                            <div>
                              Klar: {new Date(rev.completed_at || rev.updated_at || rev.started_at).toLocaleString('sv-SE')}
                            </div>
                          </div>
                        )}
                        {rev.has_report && (
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => viewReport(rev.id)}
                            className="w-full mt-2"
                          >
                            <FileText className="h-4 w-4 mr-2" />
                            Slutrapport tillgänglig
                          </Button>
                        )}
                      </div>
                    ))}
                </div>
              ) : (
                <div className="text-center text-muted-foreground py-8">
                  Inga färdigställda tester ännu
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Real-time Statistics - under hela gridet */}
        <Card className="max-h-80 overflow-y-auto">
          <CardHeader>
            <CardTitle>Realtidsstatistik</CardTitle>
            <CardDescription>
              Live-uppdatering av pågående AI-analyser
            </CardDescription>
          </CardHeader>
          <CardContent>
            {stats?.revisions && stats.revisions.length > 0 ? (
              <div className="space-y-4">
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div>
                    <div className="text-2xl font-bold text-blue-600">{stats.active_revisions}</div>
                    <div className="text-sm text-muted-foreground">Aktiva analyser</div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-green-600">{stats.total_cost?.toFixed(2) || '0.00'} SEK</div>
                    <div className="text-sm text-muted-foreground">AI-kostnad</div>
                  </div>
                  <div>  
                    <div className="text-2xl font-bold text-purple-600">{stats.total_tokens?.toLocaleString() || '0'}</div>
                    <div className="text-sm text-muted-foreground">AI Tokens</div>
                  </div>
                </div>

                {/* Visa endast pågående revisioner här */}
                {(stats.revisions || [])
                  .filter((r: any) => r.status !== 'completed' && r.status !== 'cancelled')
                  .map((rev: any) => {
                    const isCompleted = rev.status === 'completed' || rev.progress?.percentage === 100;
                    return (
                    <div key={`${rev.id}_ongoing`} className={`border rounded p-3 ${isCompleted ? 'bg-green-50 border-green-200' : ''}`}>
                      <div className="flex justify-between items-center mb-2">
                        <span className="font-medium">{rev.company_name}</span>
                        <Badge variant={
                          rev.status === 'completed' ? 'default' : 
                          rev.status === 'failed' ? 'destructive' : 
                          'secondary'
                        }>
                          {rev.status}
                        </Badge>
                      </div>
                      <Progress value={rev.progress?.percentage || 0} className="mb-2" />
                      
                      {/* Steg 6: Visa kö-position (token-baserad estimering) */}
                      {queuePosition && queuePosition.position > 1 && rev.id === activeRevisions[0] && (
                        <div className="mb-2 p-2 bg-blue-50 border border-blue-200 rounded-md">
                          <div className="flex items-center gap-2 text-xs text-blue-800">
                            <span className="font-semibold">📍 Position i kö: #{queuePosition.position}</span>
                            <span>•</span>
                            <span>⏱️ Uppskattat: ~{queuePosition.estimated_wait_minutes} min</span>
                            <span>•</span>
                            <span>🔧 Total TPM: {(queuePosition.total_tpm / 1000).toFixed(0)}k</span>
                          </div>
                        </div>
                      )}
                      
                      <div className="text-xs text-muted-foreground space-y-1">
                        <div className="flex justify-between">
                          <span>Batchar: {rev.progress?.batches_completed || 0}/{rev.progress?.batches_total || 0}</span>
                          <span>{rev.progress?.percentage || 0}%</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Kostnad: {Number(rev.total_cost_sek || 0).toFixed(2)} SEK</span>
                          <span>Tokens: {((Number(rev.total_tokens_in) || 0) + (Number(rev.total_tokens_out) || 0)).toLocaleString()}</span>
                        </div>
                        {rev.next_retry_after && (
                          <div className="text-orange-600">
                            ⏳ Nästa försök: {new Date(rev.next_retry_after).toLocaleTimeString('sv-SE')}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center text-muted-foreground py-8">
                Inga aktiva analyser för tillfället
              </div>
            )}
          </CardContent>
        </Card>

        {dummyReport && (
          <Card>
            <CardHeader>
              <CardTitle>AI-Analysrapport</CardTitle>
              <CardDescription>
                Genererad av Azure OpenAI baserat på riktig skattekontodata
              </CardDescription>
            </CardHeader>
            <CardContent>
              <iframe
                title="AI Analysrapport"
                srcDoc={dummyReport}
                sandbox="allow-same-origin"
                className="w-full h-96 rounded border bg-white"
              />
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
